import { Reorder, useDragControls } from "framer-motion";
import { GripVertical } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

const STUDY_DESC = ["purpose", "response", "historical_context", "commentary", "commentaries"];

function FieldRow({ f, shown, onToggle, labels, defaults, onLabel }) {
  const editable = !!defaults[f.key];
  const cur = labels[f.key] || {};
  const dirty = editable && (cur.label !== defaults[f.key].label || (STUDY_DESC.includes(f.key) && cur.desc !== defaults[f.key].desc));
  return (
    <li className="space-y-2 rounded-xl border border-border px-4 py-3" data-testid={`page-field-row-${f.key}`}>
      <div className="flex items-center justify-between gap-3">
        <span className={`text-sm font-medium ${shown ? "text-foreground" : "text-muted-foreground line-through"}`}>{f.label}</span>
        <div className="flex items-center gap-3">
          {dirty && (
            <button type="button" onClick={() => onLabel(f.key, defaults[f.key])} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid={`page-field-reset-${f.key}`}>Reset</button>
          )}
          <Switch checked={shown} onCheckedChange={onToggle} data-testid={`page-field-${f.key}`} aria-label={`Show ${f.label}`} />
        </div>
      </div>
      {editable && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input value={cur.label || ""} placeholder={defaults[f.key].label} onChange={(e) => onLabel(f.key, { ...cur, label: e.target.value })} className="h-9" data-testid={`page-field-label-${f.key}`} aria-label={`${f.label} heading`} />
          {STUDY_DESC.includes(f.key) && (
            <Input value={cur.desc || ""} placeholder="Short description (optional)" onChange={(e) => onLabel(f.key, { ...cur, desc: e.target.value })} className="h-9" data-testid={`page-field-desc-${f.key}`} aria-label={`${f.label} description`} />
          )}
        </div>
      )}
    </li>
  );
}

function OrderItem({ k, name, hidden }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={k} dragListener={false} dragControls={controls} data-testid={`study-order-${k}`}
      className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 text-sm">
      <button type="button" onPointerDown={(e) => controls.start(e)} className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing" aria-label={`Drag ${name}`} data-testid={`study-order-handle-${k}`}>
        <GripVertical className="h-4 w-4" />
      </button>
      <span className={hidden ? "text-muted-foreground line-through" : "text-foreground"}>{name}</span>
      {hidden && <span className="ml-auto text-xs text-muted-foreground">hidden</span>}
    </Reorder.Item>
  );
}

export function PageFieldsEditor({ fields, hidden, onChange, labels, defaults, onLabels, order, onOrder }) {
  const toggle = (key, show) => onChange(show ? hidden.filter((k) => k !== key) : [...hidden, key]);
  const setLabel = (key, v) => onLabels({ ...labels, [key]: v });
  return (
    <section className="space-y-6 rounded-2xl border border-border bg-card p-5" data-testid="settings-page-fields">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-foreground">Question page sections</h2>
        <p className="mt-1 text-sm text-muted-foreground">Show, hide or rename each part of every question page. Hidden content stays saved and editable in Admin.</p>
      </div>
      <ul className="grid gap-2 lg:grid-cols-2">
        {fields.map((f) => (
          <FieldRow key={f.key} f={f} shown={!hidden.includes(f.key)} onToggle={(v) => toggle(f.key, v)} labels={labels} defaults={defaults} onLabel={setLabel} />
        ))}
      </ul>
      <div>
        <h3 className="font-serif text-lg font-semibold text-foreground">Study tab order</h3>
        <p className="mb-3 text-sm text-muted-foreground">Drag to reorder the parts of the Study tab.</p>
        <Reorder.Group axis="y" values={order} onReorder={onOrder} className="max-w-md space-y-2" data-testid="study-order">
          {order.map((k) => <OrderItem key={k} k={k} name={labels[k]?.label || defaults[k]?.label || k} hidden={hidden.includes(k)} />)}
        </Reorder.Group>
      </div>
    </section>
  );
}
